import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { GridInput } from '@it2go/data-grid';
import { filterFrag } from './types/filter.types';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { MessageGridObject, MessageObject } from '@libs/types/src/user/object-type/message.object';
import { MessageRecipientGridObject } from '@libs/types/src/user/object-type/message-recipient.object';
import { MessageCreateInput } from '@libs/types/src/user/input-type/user/message.create-input';

@Injectable({ providedIn: 'root' })
export class MessageGqlService extends GqlServiceV2 {

    public async getUserMessages(input: GridInput, fragment: string): Promise<MessageGridObject> {
        return this.query<MessageGridObject>(
            `
                query($input: GridInput!) {
                    message {
                        userMessageFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getMethodicalMessages(input: GridInput, fragment: string): Promise<MessageGridObject> {
        return this.query<MessageGridObject>(
            `
                query($input: GridInput!) {
                    message {
                        methodicalMessageFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getServiceMessages(input: GridInput, fragment: string): Promise<MessageGridObject> {
        return this.query<MessageGridObject>(
            `
                query($input: GridInput!) {
                    message {
                        serviceMessageFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createUserMessage(input: MessageCreateInput, fragment: string): Promise<MessageObject> {
        return this.mutate<MessageObject>(
            `
                mutation($input: MessageCreateInput!) {
                    message {
                        createUserMessage(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createMethodicalMessage(input: MessageCreateInput, fragment: string): Promise<MessageObject> {
        return this.mutate<MessageObject>(
            `
                mutation($input: MessageCreateInput!) {
                    message {
                        createMethodicalMessage(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createServiceMessage(input: MessageCreateInput, fragment: string): Promise<MessageObject> {
        return this.mutate<MessageObject>(
            `
                mutation($input: MessageCreateInput!) {
                    message {
                        createServiceMessage(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteUserMessage(input: IdInput): Promise<OkObject> {
        return this.deleteMessage('deleteUserMessage', input);
    }

    public async deleteMethodicalMessage(input: IdInput): Promise<OkObject> {
        return this.deleteMessage('deleteMethodicalMessage', input);
    }

    public async deleteServiceMessage(input: IdInput): Promise<OkObject> {
        return this.deleteMessage('deleteServiceMessage', input);
    }

    private async deleteMessage(field: string, input: IdInput): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    message {
                        ${field}(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getMessageRecipients(input: GridInput, fragment: string): Promise<MessageRecipientGridObject> {
        return this.query<MessageRecipientGridObject>(
            `
                query($input: GridInput!) {
                    messageRecipient {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getMyMessageRecipients(input: GridInput, fragment: string): Promise<MessageRecipientGridObject> {
        return this.query<MessageRecipientGridObject>(
            `
                query($input: GridInput!) {
                    messageRecipient {
                        userProfileFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteMessageRecipient(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    messageRecipient {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async markMessageRecipientAsRead(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    messageRecipient {
                        markAsRead(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

}
